import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';
import { Check, X, FileEdit, Loader2 } from 'lucide-react';

const renderDiffLine = (line, idx) => {
  let cls = 'text-foreground';
  if (line.startsWith('+++') || line.startsWith('---')) cls = 'text-muted-foreground font-semibold';
  else if (line.startsWith('@@')) cls = 'text-violet-500 font-mono';
  else if (line.startsWith('+')) cls = 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  else if (line.startsWith('-')) cls = 'bg-red-500/10 text-red-700 dark:text-red-300';
  return (
    <div key={idx} className={`px-2 py-0.5 font-mono text-[11px] leading-tight whitespace-pre ${cls}`}>
      {line || ' '}
    </div>
  );
};

export const DocumentationDiffViewer = ({ proposalId, open, onClose, onApplied }) => {
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');

  useEffect(() => {
    if (!open || !proposalId) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await tl.listProposals();
        const p = (data.items || []).find((x) => x.proposal_id === proposalId);
        if (p) {
          setProposal(p);
          setEditedContent(p.proposed_content);
        }
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [proposalId, open]);

  const reject = async () => {
    setActing(true);
    try {
      await tl.decideProposal(proposalId, 'reject');
      toast.success('Proposal rejected');
      onClose && onClose();
      onApplied && onApplied();
    } catch (e) {
      toast.error(e.message);
    } finally { setActing(false); }
  };

  const apply = async (alsoEdit = false) => {
    setActing(true);
    try {
      if (alsoEdit && editedContent !== proposal.proposed_content) {
        // Create a new proposal with the edited content
        const newProp = await tl.createProposal({
          documentation_file_id: proposal.documentation_file_id,
          section_id: proposal.section_id,
          proposed_content: editedContent,
          reason_for_change: `${proposal.reason_for_change || ''} (edited by reviewer)`,
          source_type: 'manual_request',
        });
        await tl.applyProposal(newProp.proposal_id);
        await tl.decideProposal(proposalId, 'reject', 'superseded by edited proposal');
      } else {
        await tl.applyProposal(proposalId);
      }
      toast.success('Documentation updated');
      onClose && onClose();
      onApplied && onApplied();
    } catch (e) {
      toast.error(e.message);
    } finally { setActing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="doc-diff-viewer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Documentation change proposal
            {proposal?.proposed_by === 'akro' && <Badge className="bg-violet-500">Akro</Badge>}
            <Badge variant="outline">{proposal?.status}</Badge>
          </DialogTitle>
        </DialogHeader>

        {loading || !proposal ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col gap-3">
            {proposal.reason_for_change && (
              <div className="bg-muted/50 border border-border rounded p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Reason</div>
                <p className="text-xs">{proposal.reason_for_change}</p>
              </div>
            )}

            {(proposal.affected_sections || []).length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-1">
                  Related sections to also review
                </div>
                <ul className="text-xs space-y-0.5">
                  {proposal.affected_sections.map((s, i) => <li key={i}>• {s}</li>)}
                </ul>
              </div>
            )}

            {proposal.consistency_notes && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-400 mb-1">
                  Consistency notes
                </div>
                <p className="text-xs">{proposal.consistency_notes}</p>
              </div>
            )}

            {!editing ? (
              <ScrollArea className="flex-1 border border-border rounded bg-muted/30">
                <div className="py-2">
                  {(proposal.diff || '').split('\n').map(renderDiffLine)}
                </div>
              </ScrollArea>
            ) : (
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="flex-1 font-mono text-xs"
                data-testid="doc-diff-edit"
              />
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => setEditing(!editing)}
            disabled={acting || !proposal}
            data-testid="doc-diff-edit-toggle"
          >
            <FileEdit className="w-4 h-4 mr-1" />
            {editing ? 'View diff' : 'Edit before applying'}
          </Button>
          <Button
            variant="outline"
            onClick={reject}
            disabled={acting || !proposal || proposal.status === 'applied' || proposal.status === 'rejected'}
            data-testid="doc-diff-reject"
          >
            <X className="w-4 h-4 mr-1" /> Reject
          </Button>
          <Button
            onClick={() => apply(editing)}
            disabled={acting || !proposal || proposal.status === 'applied' || proposal.status === 'rejected'}
            data-testid="doc-diff-apply"
          >
            {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            {editing ? 'Apply edited' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentationDiffViewer;
